// app/src/shared/components/theme/commonStylesColor.ts
/**
 * 공통 컬러 팔레트
 * 프로젝트 전체에서 사용되는 색상 값을 중앙 관리
 */

// ============= 기본 색상 =============
export const colors = {
  // 순수 색상
  white: "#fff",
  black: "#000",

  // 그레이 스케일
  gray: {
    50: "#f5f5f5",
    100: "#eee",
    200: "#e0e0e0",
    300: "#ccc",
    400: "#999",
    500: "#888",
    600: "#666",
    700: "#555",
    800: "#444",
    900: "#333",
  },

  // 다크 그레이 스케일
  darkGray: {
    100: "#6b6b6b",
    200: "#777",
  },
} as const

// ============= 브랜드 색상 =============
export const brandColors = {
  primary: {
    main: "#0196da",
    light: "#009aef",
    dark: "#0088d1",
  },
  secondary: {
    main: "#6cbba9",
    light: "#77e3f7",
  },
  accent: {
    pink: "#F19ca3",
    blue: "#a9b7c2",
  },
  button: {
    reception: "#F19ca3",
    payment: "#D4AE9A",
    reservation: "#a9b7c2",
  },
} as const

// ============= 상태 색상 =============
export const statusColors = {
  info: "#0288d1",
  warning: "#ed6c02",
  error: {
    main: "#d32f2f",
    dark: "#b71c1c",
  },
  success: "#2e7d32",
} as const

// ============= 보험 타입 색상 =============
export const insuranceColors = {
  light: {
    건보: "rgba(105, 105, 105, 0.3)",
    자보: "rgba(172, 117, 117, 0.3)",
    산재: "rgba(224, 102, 21, 0.3)",
    일반: "rgba(149, 201, 228, 0.2)",
  },
  dark: {
    건보: "rgba(160, 160, 160, 0.4)",
    자보: "rgba(220, 150, 150, 0.4)",
    산재: "rgba(255, 140, 60, 0.4)",
    일반: "rgba(180, 220, 255, 0.3)",
  },
  text: {
    light: colors.gray[900],
    dark: colors.gray[200],
  },
} as const

// ============= 텍스트 색상 =============
export const textColors = {
  primary: "#222",
  secondary: "#5b90b7",
  navigation: "#334155",
  informationTitle: "#26789e",
  dashboardTitle: colors.gray[800],
  muted: colors.gray[500],
} as const

// ============= 배경 색상 =============
export const backgroundColors = {
  default: "#F9FAFB",
  paper: colors.white,
  global: "#f2f3efff",
  information: "#eddfdf",
  subInfo: "#F9FAFB",
  encounter: "#ebf4fc",
  encounterContents: colors.white,

  // 테이블 관련
  tableHeader: colors.gray[50],
  tableEven: "#e3f2fd",
  tableHover: "#bbdefb",
  tableRefund: "#fde7ef",

  // 에디터 배경
  editable: {
    light: "#eef",
    blue: "#cde4faff",
    gray: "#efefef",
  },
} as const

// ============= 파스텔 색상 =============
export const pastelColors = {
  background: "#F0F8FF",
  header: "#DCEEFF",
  row: "#EAF4FF",
  text: colors.gray[900],
  hover: "#6b85a1",
  table: "#5f91bd",
  insuranceBenefit: "#5f8de6",
  insuranceBenefitText: "#ffffff",
} as const

// ============= 특수 UI 색상 =============
export const uiColors = {
  // 새창 공통 메뉴 및 주요 액션
  detachedWindow: {
    background: backgroundColors.global,
    primary: brandColors.primary.main,
    primaryHover: "rgb(0, 105, 152)",
    secondary: "#36b3c7",
    secondaryHover: "#2996a8",
    highlight: statusColors.success,
    highlightHover: "#1b5e20",
    soft: "#eaf7fc",
    softHover: "#d8f0fa",
    border: "#8bcce8",
    text: "#0277a8",
    menuText: textColors.navigation,
    menuTextHover: "#123a63",
    icon: brandColors.primary.dark,
  },

  // 윈도우 컨트롤
  windowControl: {
    close: "#FF5F57",
    minimize: "#FFBD2E",
    maximize: "#28CA42",
  },

  // 테마별 border
  border: {
    light: "#c0c0c0",
    dark: "#404040",
    default: colors.gray[300],
  },

  // hover 효과
  hover: {
    transition: "#1976d2",
    blue: "#4a90e2",
  },

  // 레이어/모달 헤더
  layerHeader: {
    activeBackground: "linear-gradient(180deg, #d9edf9 0%, #bfdcf0 100%)",
    activeBorder: "#9fc4dc",
    activeText: "#27445a",
  },

  // 아이콘 색상
  icon: {
    neutral: "#CC6699",
    reservationTimeUnitBlue: "#0196da",
    waitingOrderAction: "#949494",
  },

  // 태그 색상
  tag: {
    complete: "#ff7a00",
    incomplete: "#6abfce",
  },

  // 예약 색상
  reservation: colors.darkGray[200],
} as const

// ============= 다크모드 색상 =============
export const darkModeColors = {
  background: {
    global: "#1a1a1a",
    panel: "#ffffff", // 다크모드에서도 흰색 패널 유지
    information: "#3a3a3a",
    button: "#2d2d2d",
    cardNormal: "#2d2d2d",
    cardSelected: "#4a90e2",
    subInfo: "#232323",
    encounter: "#2a3f4f",
    encounterContents: "#a1a1a1",
    infoTable: "#2d2d2d",
  },
  text: {
    main: colors.white,
    sub: colors.gray[200],
    informationTitle: "#6bb6ff",
    dashboardTitle: "#c0c0c0",
  },
  border: "#404040",
  historyAccent: "#e08a8a",
  informationTitle: "#7bb5f0",
  transitionHover: "#4a90e2",
} as const

// ============= 타입 정의 =============
export type Colors = typeof colors
export type BrandColors = typeof brandColors
export type StatusColors = typeof statusColors
export type InsuranceColors = typeof insuranceColors
export type TextColors = typeof textColors
export type BackgroundColors = typeof backgroundColors
export type PastelColors = typeof pastelColors
export type UiColors = typeof uiColors
export type DarkModeColors = typeof darkModeColors
